import { useNavigate } from 'react-router-dom';
import { useAssessmentApi } from '../../api/assessment';
import { DemographicsButtonToggle } from './guestDemographics';
import GuestBase from './guestBase';
import { Grid, Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider, DateField } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useState, useEffect, useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

export const GuestSymptomChecker = () => {
  const navigate = useNavigate();
  const api = useAssessmentApi();

  const [symptomsExist, setSymptomsExist] = useState(null);
  const [symptomDescription, setSymptomDescription] = useState('');
  const [symptomStarted, setSymptomStarted] = useState(null); // it will be a dayjs() object

  const [isDateValidated, setIsDateValidated] = useState(true);
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const descriptionStrengthLevels = {
    Empty: {
      title: '',
      color: '',
      text: '',
    },
    Low: {
      title: 'Low',
      color: 'red',
      text: "Your description could use a little more detail. Tell us as much as you can about the symptoms you're experiencing.",
    },
    Average: {
      title: 'Good',
      color: '#F2C01B',
      text: 'Good description! If you can provide more details, that would be great. If not, no problem.',
    },
    High: {
      title: 'Excellent',
      color: '#A6E177',
      text: 'This is a very detailed description. All this information makes our work much easier.',
    },
  };

  //checking symptom description length
  const descriptionStrength = useMemo(() => {
    if (symptomDescription.length === 0) return descriptionStrengthLevels.Empty;
    if (symptomDescription.length < 70) return descriptionStrengthLevels.Low;

    if (symptomDescription.length >= 70 && symptomDescription.length <= 1000)
      return descriptionStrengthLevels.Average;

    return descriptionStrengthLevels.High;
  }, [symptomDescription]);

  useEffect(() => {
    if (symptomStarted === null || symptomStarted === '' || symptomStarted?.format() === 'Invalid Date') return;
    const startDateString = symptomStarted?.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    sessionStorage.setItem('symptom-checker-startDate', startDateString);
  }, [symptomStarted]);

  const handleNext = async () => {
    if (symptomsExist) {
      await api.postAnonAssessmentDiagnose({
        symptoms_description: symptomDescription,
        symptoms_started_at: symptomStarted,
      });
    }
    navigate('/email-page');
  };

  return (
    <GuestBase onPrev={() => navigate('/guest-pre-face-scan')} onNext={handleNext}>
      <Stack sx={{p: 3, textAlign: 'left'}} spacing={2}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 30,
          }}
        >
          Let's start the symptom checking process.
        </Typography>
        <Typography>Are you experiencing any symptoms today?</Typography>
        <DemographicsButtonToggle
          value={symptomsExist}
          onChange={setSymptomsExist}
          options={[
            {name: 'Yes', id: true},
            {name: 'No', id: false},
          ]}
          selected={symptomsExist}
        />
        {symptomsExist && (
          <Stack spacing={2}>
            <Typography>Can you tell us more about the symptoms you are experiencing?</Typography>
            <TextField
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 5,
                  borderWidth: 0,
                  backgroundColor: '#EDF9F980',
                  height: 200,
                  alignItems: 'flex-start',
                },
                '& fieldset': {
                  borderWidth: 0,
                },
              }}
              placeholder="I have a sore throat, runny nose..."
              value={symptomDescription}
              onChange={e => setSymptomDescription(e.target.value)}
              fullWidth
              multiline
              rows={8}
            />
            <Typography variant="body1" pt={1}>
              <span style={{fontWeight: 'bold'}}>Description strength:</span>{' '}
              <span
                style={{
                  fontWeight: 'bold',
                  color: descriptionStrength.color,
                }}
              >
                {descriptionStrength.title}
              </span>
              <br />
              <span>{descriptionStrength.text}</span>
            </Typography>
            <Grid item xs={12} md={5} sx={{ marginTop: 4 }}>
              <Typography variant="subtitle0" textAlign="center">
                When did this start?
              </Typography>
            </Grid>
            <Grid item xs={12} md={5} sx={{ marginTop: 1, width: '100%' }}>
              <Stack >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  {isSmallScreen ? (
                    <DateField
                      disableFuture
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          sx: {
                            '.MuiInput-input': {
                              textAlign: 'center',
                            },
                          },
                        },
                      }}
                      value={symptomStarted === null || symptomStarted === '' ? null : symptomStarted}
                      onChange={update => {
                        setSymptomStarted(update);
                      }}
                      isClearable={true}
                    />
                  ) : (
                    <DatePicker
                      disableFuture
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          fullWidth: true,
                        },
                        popper: {
                          sx: {
                            '& .MuiPickersPopper-paper': {
                              textAlign: 'center',
                              borderRadius: '10px !important',
                            },
                          },
                          placement: 'auto',
                        },
                      }}
                      sx={{
                        '& .MuiInput-input': {
                          textAlign: 'center',
                          ml: isSmallScreen ? 0 : 4,
                        },
                      }}
                      value={symptomStarted === null || symptomStarted === '' ? null : symptomStarted}
                      onChange={update => {
                        setSymptomStarted(update);
                      }}
                      isClearable={true}
                    />
                  )}
                </LocalizationProvider>
                {(() => {
                  if (isDateValidated) return;

                  let errorMessage = '';
                  if (symptomStarted === null || symptomStarted === undefined || symptomStarted === '') errorMessage = 'Choose a date';
                  else if (symptomStarted.isAfter(dayjs())) errorMessage = "You can't choose a date in the future!";

                  return (
                    <Typography variant="caption" color="error" display="block" gutterBottom>
                      {errorMessage}
                    </Typography>
                  );
                })()}
              </Stack>
            </Grid>
          </Stack>
        )}
      </Stack>
    </GuestBase>
  );
};
